import { useCallback, useEffect, useState } from 'react'
import { applyEdgeChanges, applyNodeChanges } from '@xyflow/react'
import { getAddNodeMutations } from '../../helpers'
import { useJourneyContext } from '../../context'
import { AddStepType, JourneyEdge } from '../../types/journey-nodes'
import type { IActionStep, IDelayStep } from '@pushly/models/lib/structs/journeys'
import { CopyTypes, JourneyAction } from '../../types/journey-context'
import { useAddStepOptions } from '../../hooks/use-add-step-options'

export interface UseEdgeActionsParams {
    source: string
    target: string
    variantId?: string
}

export interface UseEdgeActionsReturn {
    edgeActionOptions: AddStepType[]
    showEdgeActions: boolean
    setShowEdgeActions: (show: boolean) => void
    handleAddNode: (
        stepType: AddStepType,
        addConfig?: {
            override?: IActionStep | IDelayStep
            overrideType?: CopyTypes
            skipOpenEditor?: boolean
        },
    ) => void
}

export function useEdgeActions({ source, target, variantId }: UseEdgeActionsParams): UseEdgeActionsReturn {
    const [state, dispatch] = useJourneyContext()
    const nodes = Array.isArray(state.nodes) ? state.nodes : []
    const edges = Array.isArray(state.edges) ? state.edges : []

    const edgeActionOptions = useAddStepOptions(source)
    const [showEdgeActions, setShowEdgeActionsRaw] = useState(false)

    // Track the signal value at the time we last opened, so we can
    // ignore the close signal that our own open triggered.
    const [openedAtSignal, setOpenedAtSignal] = useState(0)

    const setShowEdgeActions = useCallback(
        (show: boolean) => {
            if (show) {
                // Close all other popovers first, then open ours
                dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
                // Record the signal value AFTER our dispatch so we skip
                // the resulting close effect for this instance
                setOpenedAtSignal(state.closePopoversSignal + 1)
            }
            setShowEdgeActionsRaw(show)
        },
        [dispatch, state.closePopoversSignal],
    )

    // Watch for close popover signal — but skip if we just opened
    useEffect(() => {
        if (state.closePopoversSignal > 0 && state.closePopoversSignal !== openedAtSignal) {
            setShowEdgeActionsRaw(false)
        }
    }, [state.closePopoversSignal, openedAtSignal])

    const handleAddNode = useCallback(
        (
            stepType: AddStepType,
            addConfig: { override?: IActionStep | IDelayStep; overrideType?: CopyTypes; skipOpenEditor?: boolean } = {},
        ) => {
            setShowEdgeActions(false)

            const sourceNode = nodes.find((n) => n.id === source)
            const targetNode = nodes.find((n) => n.id === target)

            if (sourceNode && targetNode) {
                const [nodeChanges, edgeChanges, newNode] = getAddNodeMutations(
                    stepType,
                    nodes,
                    sourceNode,
                    targetNode,
                    addConfig.override,
                    addConfig.overrideType,
                    variantId,
                    edges as JourneyEdge[],
                )

                const updatedNodes = applyNodeChanges(nodeChanges, nodes)
                const updatedEdges = applyEdgeChanges(edgeChanges, edges)

                dispatch({
                    action: JourneyAction.ON_JOURNEY_CHANGES,
                    data: { nodes: updatedNodes, edges: updatedEdges },
                })

                if (!addConfig.skipOpenEditor) {
                    if (newNode) {
                        dispatch({ action: JourneyAction.ON_NODE_ADD, data: newNode })
                    }
                }
            }
        },
        [source, target, variantId, nodes, edges, dispatch],
    )

    return {
        edgeActionOptions,
        showEdgeActions,
        setShowEdgeActions,
        handleAddNode,
    }
}
